import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';

import Page from 'components/Page';
import Loading from 'components/Loading';
import {Container} from 'containers/types';
import ListContent from 'components/ListContent';
import VisibleIf from 'components/VisibleIf';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {ConfirmationModal} from 'components/CommonModals';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';

import {exportCsv, selectExporting} from 'ducks/files';
import {composeActions} from 'ducks/common';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {isModalOpen, MODALS as BASE_MODALS, openModal} from 'ducks/modals';
import {
  fetchInvoicesForReporting,
  selectInvoicesForReporting,
  selectAreInvoicesForReportingLoading,
  initiateGoCardlessPaymentsFlow,
  MODALS
} from 'ducks/reporting';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import {getUpdatedFilter} from 'utils/filters';

import {
  InvoiceReportingTable,
  ExcludeInvoicesModal,
  PreviewInvoicesModal,
  SubNavigation,
  InvoiceReportingFilters
} from './components';

import './styles.css';

const initialFilters: {
  search: string;
  scheduleTypes: string[];
  statuses: string[];
  xeroStatuses: string[];
  booking: number | null;
  bookingStatuses: string[];
  customer: number | null;
  createdBy: number | null;
  createdByTeam: number | null;
  dueDateStart: string | null;
  dueDateEnd: string | null;
  dateIssuedStart: string | null;
  dateIssuedEnd: string | null;
  createdAtStart: string | null;
  createdAtEnd: string | null;
  rentStartStart: string | null;
  rentStartEnd: string | null;
  rentEndStart: string | null;
  rentEndEnd: string | null;
  goCardlessChargeable: boolean;
} = {
  search: '',
  scheduleTypes: [],
  statuses: [],
  xeroStatuses: [],
  booking: null,
  bookingStatuses: [],
  customer: null,
  createdBy: null,
  createdByTeam: null,
  dueDateStart: null,
  dueDateEnd: null,
  dateIssuedStart: null,
  dateIssuedEnd: null,
  createdAtStart: null,
  createdAtEnd: null,
  rentStartStart: null,
  rentStartEnd: null,
  rentEndStart: null,
  rentEndEnd: null,
  goCardlessChargeable: false
};

const INVOICES_CSV_FILENAME = 'invoices';

class InvoiceReporting extends React.Component<
  Container &
    LoginRequiredProps &
    WithFiltersProps<typeof initialFilters> &
    WithSortingProps &
    WithPaginationProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_INVOICE_REPORTING,
        locationRef
      },
      () => this.props.fetchInvoicesForReporting({locationRef})
    );
  }

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, offset}, [
      () => fetchInvoicesForReporting({locationRef})
    ]);
  };

  openConfirmationModal = ({heading, confirmAction, question, ...args}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction,
      ...args
    });

  filterInvoicesBy = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    const updatedFilter = getUpdatedFilter(
      {field, value},
      this.props.getFilters()
    );

    this.props.composeActions([
      () => this.props.setFilter(updatedFilter.field, updatedFilter.value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchInvoicesForReporting({locationRef})
    ]);
  };

  onSort = fieldName => {
    const {match: {params: {locationRef}}} = this.props;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () => fetchInvoicesForReporting({locationRef}),
      () =>
        this.props.onPaginationChange({
          offset: 0,
          pageSize: pagination.pageSize
        })
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchInvoicesForReporting({locationRef})
    ]);
  };

  initiateGoCardlessPaymentsFlow = ({invoicesIds}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.INITIATE_GOCARDLESS_PAYMENT, locationRef},
      () => this.props.initiateGoCardlessPaymentsFlow({invoicesIds})
    );
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    // open booking detail in new tab
    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(
          reverse(BOOKING_DETAIL_URL, {
            locationRef,
            bookingId
          })
        );
      }
    );
  };

  redirectToScheduleItem = ({link}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        window.open(link);
      }
    );
  };

  render() {
    const {
      areInvoicesLoading,
      invoices,
      match: {params: {locationRef}},
      excludeInvoicesFromPaymentInitiationModal,
      previewInvoicesForPaymentInitiaionModal,
      confirmationModal
    } = this.props;

    return (
      <Page activeTab="invoices" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_INVOICE_REPORTING}
          locationRef={locationRef}>
          <ListContent
            className="InvoiceReporting"
            filters={this.props.getFilters()}
            setFilter={this.filterInvoicesBy}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {areInvoicesLoading && <Loading />}
              {!areInvoicesLoading && (
                <InvoiceReportingTable
                  invoices={invoices}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  initiateGoCardlessPaymentsFlow={
                    this.initiateGoCardlessPaymentsFlow
                  }
                  locationRef={locationRef}
                  exportCsv={this.props.exportCsv}
                  sort={this.props.getSort()}
                  sortBy={this.onSort}
                  redirectToBookingDetail={this.redirectToBookingDetail}
                  redirectToScheduleItem={this.redirectToScheduleItem}
                  filters={this.props.getFilters()}
                  exportingInvoices={this.props.exportingInvoices}
                  invoicesCsvFilename={INVOICES_CSV_FILENAME}
                />
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <InvoiceReportingFilters
                locationRef={locationRef}
                setFilter={this.filterInvoicesBy}
                filters={this.props.getFilters()}
                clearFilters={this.clearFilters}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>

        {excludeInvoicesFromPaymentInitiationModal && <ExcludeInvoicesModal />}

        {previewInvoicesForPaymentInitiaionModal && (
          <PreviewInvoicesModal
            openConfirmationModal={this.openConfirmationModal}
            locationRef={locationRef}
          />
        )}

        {confirmationModal && <ConfirmationModal />}
      </Page>
    );
  }
}

const mapStateToProps = state => {
  return {
    areInvoicesLoading: selectAreInvoicesForReportingLoading(state),
    invoices: selectInvoicesForReporting(state),
    confirmationModal: isModalOpen(state, BASE_MODALS.CONFIRM),
    excludeInvoicesFromPaymentInitiationModal: isModalOpen(
      state,
      MODALS.EXCLUDED_INVOICES_FROM_PAYMENT_INITIATION
    ),
    previewInvoicesForPaymentInitiaionModal: isModalOpen(
      state,
      MODALS.PREVIEW_INVOICES_FOR_PAYMENT_INITIATION
    ),
    exportingInvoices: selectExporting(state, INVOICES_CSV_FILENAME)
  };
};

const mapDispatchToProps = {
  fetchInvoicesForReporting,
  requiresPermission,
  initiateGoCardlessPaymentsFlow,
  openModal,
  composeActions,
  exportCsv,
  push
};

const componentWithSorting = withSorting(
  LoginRequired(InvoiceReporting),
  SORTING.INVOICE_REPORTING_LIST,
  {
    saveToUrl: true
  }
);

const componentWithSortingAndPagination = withPagination(
  componentWithSorting,
  PAGINATION.INVOICE_REPORTING_LIST,
  {saveToUrl: true}
);

const componentWithSortingPaginationAndFilters = withFilters(
  componentWithSortingAndPagination,
  FILTERS.INVOICE_REPORTING_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  componentWithSortingPaginationAndFilters
);
